import { Document, isMap, isScalar, isSeq, parseDocument, Scalar, type YAMLSeq } from "yaml";

const BOM = "﻿";

/**
 * Everything needed to put a document back together byte for byte:
 * the YAML text, the blank lines after it, the line ending, a BOM.
 */
export interface DocumentParts {
  hasFrontMatter: boolean;
  /** A byte order mark, or "". */
  bom: string;
  eol: "\n" | "\r\n";
  /** Front matter text, without the --- lines. */
  yaml: string;
  /** "---" or "...". */
  closeDelim: string;
  /** Blank lines between the front matter and the body. */
  gap: number;
  /** Whether the original body ended with a newline. */
  trailingNewline: boolean;
  /** Body text, always with "\n" line endings. */
  body: string;
}

export function detectEol(text: string): "\n" | "\r\n" {
  return text.includes("\r\n") ? "\r\n" : "\n";
}

export function toLf(text: string): string {
  return text.replace(/\r\n?/g, "\n");
}

export function applyEol(text: string, eol: "\n" | "\r\n"): string {
  return eol === "\r\n" ? toLf(text).replace(/\n/g, "\r\n") : toLf(text);
}

/**
 * Splits a file into front matter and body. Front matter exists only when the
 * file starts with a --- line and a closing --- (or ...) line follows; a --- in
 * the body is a divider and is never touched.
 */
export function splitDocument(text: string): DocumentParts {
  let rest = text;
  let bom = "";
  if (rest.startsWith(BOM)) {
    bom = BOM;
    rest = rest.slice(1);
  }
  const eol = detectEol(rest);

  const open = /^---[ \t]*\r?\n/.exec(rest);
  if (open) {
    const closeRe = /^(---|\.\.\.)[ \t]*(?:\r?\n|$)/gm;
    closeRe.lastIndex = open[0].length;
    const close = closeRe.exec(rest);
    if (close) {
      const yaml = rest.slice(open[0].length, close.index).replace(/\r?\n$/, "");
      const after = rest.slice(close.index + close[0].length);
      const blank = /^(?:[ \t]*\r?\n)*/.exec(after)![0];
      const gap = (blank.match(/\n/g) || []).length;
      const body = toLf(after.slice(blank.length));
      return {
        hasFrontMatter: true,
        bom,
        eol,
        yaml: toLf(yaml),
        closeDelim: close[1],
        gap,
        trailingNewline: body.endsWith("\n"),
        body,
      };
    }
  }

  const body = toLf(rest);
  return {
    hasFrontMatter: false,
    bom,
    eol,
    yaml: "",
    closeDelim: "---",
    gap: 1,
    trailingNewline: body.endsWith("\n"),
    body,
  };
}

/**
 * Puts a document back together. Unchanged parts come out exactly as read.
 * `body` is the replacement body (editor output); it defaults to parts.body.
 */
export function composeDocument(parts: DocumentParts, body: string = parts.body): string {
  let text = toLf(body);
  if (parts.trailingNewline && text !== "" && !text.endsWith("\n")) {
    text += "\n";
  }
  if (!parts.trailingNewline) {
    text = text.replace(/\n+$/, "");
  }

  const head = parts.hasFrontMatter
    ? `---\n${parts.yaml}${parts.yaml === "" ? "" : "\n"}${parts.closeDelim}\n${"\n".repeat(parts.gap)}`
    : "";
  return parts.bom + applyEol(head + text, parts.eol);
}

const STRINGIFY = { lineWidth: 0, flowCollectionPadding: false };

export interface FrontMatterModel {
  doc: Document;
  errors: string[];
  /** The YAML text as read. */
  original: string;
  /** Where each top-level value sits in `original`. */
  ranges: Map<string, { start: number; end: number }>;
}

/**
 * Parses YAML text. Errors are returned, never thrown, so a half-typed raw
 * edit can be shown to the writer without losing anything.
 */
export function parseFrontMatter(yamlText: string): FrontMatterModel {
  const doc = parseDocument(yamlText);
  const errors = doc.errors.map((e) => e.message.split("\n")[0]);
  if (errors.length === 0 && doc.contents && !isMap(doc.contents)) {
    errors.push("Front matter must be a list of keys and values.");
  }

  const ranges = new Map<string, { start: number; end: number }>();
  if (errors.length === 0 && isMap(doc.contents)) {
    for (const pair of doc.contents.items) {
      const range = (pair.value as { range?: [number, number, number] } | null)?.range;
      const keyValue = (pair.key as { value?: unknown } | null)?.value;
      if (keyValue !== undefined && range) {
        ranges.set(String(keyValue), { start: range[0], end: range[1] });
      }
    }
  }
  return { doc, errors, original: yamlText, ranges };
}

/**
 * Produces the YAML text after fields were edited with writeField. Only the
 * values of the changed keys are rewritten inside the original text, so every
 * other line, comment and space stays exactly as it was. Unusual cases fall
 * back to serialising the whole document, which keeps comments and order but
 * may tidy spacing.
 */
export function renderFrontMatter(model: FrontMatterModel, changedKeys: Iterable<string>): string {
  const keys = [...changedKeys];
  if (keys.length === 0) return model.original;

  const edits: { start: number; end: number; text: string }[] = [];
  const additions: string[] = [];
  for (const key of keys) {
    const node = model.doc.get(key, true);
    const range = model.ranges.get(key);
    if (!node) return stringifyAll(model.doc);

    if (!range) {
      additions.push(renderPair(model.doc, key, node));
      continue;
    }

    const original = model.original.slice(range.start, range.end);
    if (range.start === range.end) return stringifyAll(model.doc);

    let text: string;
    if (isScalar(node)) {
      text = renderScalar(model.doc, node);
    } else if (isSeq(node) && node.flow) {
      text = renderFlowSeq(model.doc, node);
    } else if (isSeq(node) && node.items.length > 0 && original.startsWith("-")) {
      const column = columnOf(model.original, range.start);
      const lines = node.items.map((item) => `- ${renderScalar(model.doc, item as Scalar)}`);
      const tail = original.endsWith("\n") ? "\n" : "";
      text = lines.join(`\n${" ".repeat(column)}`) + tail;
    } else {
      return stringifyAll(model.doc);
    }
    edits.push({ start: range.start, end: range.end, text });
  }

  let out = model.original;
  for (const edit of edits.sort((a, b) => b.start - a.start)) {
    out = out.slice(0, edit.start) + edit.text + out.slice(edit.end);
  }
  if (additions.length > 0) {
    const separator = out === "" || out.endsWith("\n") ? "" : "\n";
    out += separator + additions.join("\n");
  }
  return out;
}

function stringifyAll(doc: Document): string {
  return doc.toString(STRINGIFY).replace(/\n$/, "");
}

function renderPair(doc: Document, key: string, node: unknown): string {
  const tmp = new Document({});
  tmp.set(
    key,
    isSeq(node) ? cloneSeq(doc, node) : isScalar(node) ? copyScalar(node) : doc.createNode(node),
  );
  return tmp.toString(STRINGIFY).replace(/\n$/, "");
}

function copyScalar(node: Scalar): Scalar {
  const copy = new Scalar(node.value);
  if (node.type) copy.type = node.type;
  return copy;
}

function renderScalar(doc: Document, node: Scalar): string {
  const fresh = doc.createNode(node.value);
  if (node.type === "QUOTE_DOUBLE" || node.type === "QUOTE_SINGLE") {
    (fresh as Scalar).type = node.type;
  }
  const tmp = new Document();
  tmp.contents = fresh;
  return tmp.toString(STRINGIFY).replace(/\n$/, "");
}

function renderFlowSeq(doc: Document, node: YAMLSeq): string {
  const tmp = new Document();
  tmp.contents = cloneSeq(doc, node);
  return tmp.toString(STRINGIFY).replace(/\n$/, "");
}

function cloneSeq(doc: Document, node: YAMLSeq): YAMLSeq {
  const seq = doc.createNode([]) as YAMLSeq;
  seq.items = node.items.map((item) => {
    if (!isScalar(item)) return item;
    const copy = new Scalar(item.value);
    if (item.type) copy.type = item.type;
    return copy;
  });
  seq.flow = node.flow === true;
  return seq;
}

function columnOf(text: string, offset: number): number {
  return offset - (text.lastIndexOf("\n", offset - 1) + 1);
}
